import { Box, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';

import { ColumnsOnlyToolbar, DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import { VariablesTitle } from '../../components/AnalyticalVariablesControls';
import { useVariableSourceList } from '../../hooks/useVariableSourceList';
import type { SelectedDatasetSource } from '../../types';

interface EmptyGridRow {
  key: string;
}

const EMPTY_ROWS: EmptyGridRow[] = [];

interface AnalyticalDatasetResultsEmptyProps {
  /** "Variables (N)", shown in the grid's toolbar. */
  variablesTitle: string;
  datasetSources: SelectedDatasetSource[];
  message: string;
}

// Placeholder for the Analytical dataset's Table view before the analysis has
// a processed run: an empty grid shaped like the real results grid -- same
// column labels as the variable list, zero rows -- plus a hint explaining why.
export const AnalyticalDatasetResultsEmpty = ({
  variablesTitle,
  datasetSources,
  message,
}: AnalyticalDatasetResultsEmptyProps) => {
  const { rows, isLoading } = useVariableSourceList(datasetSources);

  const columns = useMemo<GridColDef<EmptyGridRow>[]>(
    () =>
      rows.map((row) => ({
        field: row.key,
        // A `variable` row's title is its raw avId until the variable
        // lookup resolves -- blank the header rather than flash a UUID.
        headerName: isLoading && row.avId !== null ? '' : row.title,
        flex: 1,
        minWidth: 200,
        sortable: false,
        filterable: false,
      })),
    [rows, isLoading]
  );

  // No RTK Query hook backs this grid -- it never fetches rows -- but it
  // still reports the variable lookup's own `isLoading` so DataGrid shows
  // its native loading shimmer instead of settling on the UUID headers above.
  const useFetchGridData = useCallback(
    () => ({
      data: undefined,
      isLoading,
      isUninitialized: false,
      isFetching: false,
      error: undefined,
    }),
    [isLoading]
  );

  return (
    <Stack spacing={3} sx={{ flex: 1, minHeight: 0 }} data-testid="analysis-dataset-results-empty">
      <Box
        sx={{ width: '100%', flex: 1, minHeight: 400, display: 'flex', flexDirection: 'column' }}
      >
        <DataGridLayout id="analysis-dataset-results-empty-grid" sx={{ width: '100%', flex: 1 }}>
          <DataGrid
            id="analysis-dataset-results-empty-grid"
            columns={columns}
            getRowId={(row: EmptyGridRow) => row.key}
            mode="client"
            rows={EMPTY_ROWS}
            disableSearchParamSync
            useFetchDataGridData={useFetchGridData}
            hideFooter
            slots={{ toolbar: ColumnsOnlyToolbar }}
            slotProps={{
              toolbar: { startContent: <VariablesTitle title={variablesTitle} /> },
            }}
          />
        </DataGridLayout>
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="analysis-dataset-results-empty-hint"
      >
        {message}
      </Typography>
    </Stack>
  );
};
